import type { RuntimeConfig } from './auth';

/** A non-2xx response from the Watchtower API. `status` is the HTTP status;
 *  `message` is the server's `error` field when the body was JSON, else the raw
 *  body text. */
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The Watchtower Console reads usage and MUTATES budget rules only. GET/POST/
 *  PATCH/DELETE — never a write against usage data. */
export interface Api {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  del(path: string): Promise<void>;
}

export function makeApi(
  config: RuntimeConfig,
  getToken: () => string | null | Promise<string | null>,
): Api {
  const call = async <T>(path: string, init: RequestInit): Promise<T> => {
    // Await the getter: a sync (non-promise) return resolves immediately, so
    // sync call sites are unaffected; an async getter (ensureFreshToken)
    // refreshes-if-needed before the header is built.
    const token = await getToken();
    // No token means the auth layer could not produce one. Fail HERE rather
    // than sending the request with an empty bearer: the API would answer 401,
    // and queries.ts signs the user out on any 401 — which after a
    // rate-limited refresh (#188) would discard a refresh token that is still
    // good, undoing the whole point of keeping it. Deliberately NOT an
    // ApiError: that is the type queries.ts inspects for the sign-out branch.
    // For a genuinely dead session nothing is lost — killSession() has already
    // nulled the token in state, so the UI is signed out either way; the only
    // thing skipped is signOut()'s extra clearing of the PKCE stash and the
    // server-denied latch, neither of which a dead refresh sets.
    // THIRD case since #194 delta 4, and "signed out either way" is false of
    // it: a SOFT END whose kept access token has itself expired. There the
    // refresh token is gone but state was never nulled and nothing was
    // cleared — by design, v2.1 clears no user state — so this throw lands in
    // the console while the page still looks live. Still the right throw: an
    // ApiError here would sign the reader out, which is the very thing the
    // SOFT END exists to avoid. Recovering that session belongs to the
    // scheduled re-auth, not to this line.
    if (!token) throw new Error('No access token available; the session is not ready.');
    const res = await fetch(`${config.apiUrl}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        authorization: `Bearer ${token}`,
      },
    });
    const text = await res.text();
    if (!res.ok) {
      let msg = text;
      try { msg = (JSON.parse(text) as { error?: string }).error ?? text; } catch { /* raw */ }
      throw new ApiError(res.status, msg);
    }
    // DELETE answers 204 with an empty body — JSON.parse('') would throw.
    return (text ? JSON.parse(text) : undefined) as T;
  };
  const withBody = (method: string, body: unknown): RequestInit => ({
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return {
    get: (path) => call(path, { method: 'GET' }),
    post: (path, body) => call(path, withBody('POST', body)),
    patch: (path, body) => call(path, withBody('PATCH', body)),
    del: async (path) => { await call<void>(path, { method: 'DELETE' }); },
  };
}
